
import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../services/api';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import { formatSignedLeaveDays, leaveUsageRatePercent } from '../utils/leaveDaysFormat';
import { formatDisplayDate, formatDisplayDateTime } from '../utils/localeFormat';
import NoTranslate from '../components/NoTranslate';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** @param {{ label: string; bracket: string }} props */
function LeaveMonthBracketHeader({ label, bracket }) {
  return (
    <th className="leave-balance-mcol" scope="col">
      {label}
      <br />
      ({bracket})
    </th>
  );
}

/** @param {string|Date|null|undefined} isoOrDate */
function poolExpiresToDateInput(isoOrDate) {
  if (!isoOrDate) return '';
  const s = String(isoOrDate).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

/** @param {string|null|undefined} isoLike */
function formatScenarioDateOnly(isoLike) {
  if (isoLike == null || isoLike === '') return '—';
  const formatted = formatDisplayDate(isoLike);
  return formatted || '—';
}

/** @param {string|null|undefined} isoOrDt */
function formatScenarioDateTimeShort(isoOrDt) {
  if (isoOrDt == null || isoOrDt === '') return '—';
  const formatted = formatDisplayDateTime(isoOrDt, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  return formatted || '—';
}

/** @param {unknown} cell */
function csvEscapeCell(cell) {
  return `"${String(cell ?? '').replace(/"/g, '""')}"`;
}

/** @param {string|null|undefined} value */
function csvDateYmd(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim().slice(0, 10);
  return /^(\d{4})-(\d{2})-(\d{2})/.test(s) ? s : String(value ?? '');
}

/** @param {string|null|undefined} value */
function csvIsoOrRaw(value) {
  if (value == null || value === '') return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString();
}

function LeaveBalanceReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    department: '',
    search: ''
  });
  const [employeeSearchInput, setEmployeeSearchInput] = useState('');
  const reportFetchCompletedRef = useRef(false);
  const [departments, setDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  /** @type {[Record<string, string>, function]} grant year -> input string */
  const [editingPoolValues, setEditingPoolValues] = useState({});
  /** @type {[Record<string, string>, function]} grant year -> yyyy-mm-dd for date input */
  const [editingPoolExpires, setEditingPoolExpires] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('total');
  const [monthlyYear, setMonthlyYear] = useState(() => new Date().getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(() => new Date().getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const [individualDetailEmployee, setIndividualDetailEmployee] = useState(null);
  const [individualScenarioYear, setIndividualScenarioYear] = useState(() => new Date().getFullYear());
  const [individualScenarioMonth, setIndividualScenarioMonth] = useState(() => new Date().getMonth() + 1);
  const [individualScenarioLoading, setIndividualScenarioLoading] = useState(false);
  const [individualScenarioData, setIndividualScenarioData] = useState(null);

  useEffect(() => {
    if (activeTab !== 'total' && activeTab !== 'individual') return;
    fetchReportData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab]);

  useEffect(() => {
    if (activeTab !== 'monthly') return;

    let cancelled = false;

    const run = async () => {
      try {
        setMonthlyLoading(true);
        const params = new URLSearchParams();
        params.append('year', monthlyYear);
        params.append('month', monthlyMonth);
        if (filters.department) params.append('department', filters.department);
        if (filters.search) params.append('search', filters.search);

        const response = await api.get(`/dashboard/monthly-leave-summary?${params}`);
        if (!cancelled) {
          setMonthlyData(response.data);
        }
      } catch (error) {
        console.error('Monthly leave summary fetch error:', error);
        if (!cancelled) {
          toast.error(error.response?.data?.error || 'Failed to fetch monthly summary');
          setMonthlyData(null);
        }
      } finally {
        if (!cancelled) {
          setMonthlyLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeTab, monthlyYear, monthlyMonth, filters.department, filters.search]);

  useEffect(() => {
    if (!individualDetailEmployee) return undefined;

    let cancelled = false;

    const run = async () => {
      try {
        setIndividualScenarioLoading(true);
        const params = new URLSearchParams();
        params.append('user_id', String(individualDetailEmployee.id));
        params.append('year', String(individualScenarioYear));
        params.append('month', String(individualScenarioMonth));

        const response = await api.get(`/dashboard/individual-leave-resignation?${params}`);
        if (!cancelled) {
          setIndividualScenarioData(response.data);
        }
      } catch (error) {
        console.error('Individual resignation scenario fetch error:', error);
        if (!cancelled) {
          toast.error(error.response?.data?.error || 'Failed to load resignation scenario');
          setIndividualScenarioData(null);
        }
      } finally {
        if (!cancelled) {
          setIndividualScenarioLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [individualDetailEmployee, individualScenarioYear, individualScenarioMonth]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      const q = employeeSearchInput.trim();
      setFilters(prev => (prev.search === q ? prev : { ...prev, search: q }));
    }, 300);
    return () => clearTimeout(id);
  }, [employeeSearchInput]);

  const fetchReportData = async () => {
    try {
      if (!reportFetchCompletedRef.current) {
        setLoading(true);
      }
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.department) params.append('department', filters.department);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/dashboard/leave-balance-report?${params}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Leave balance report fetch error:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
      reportFetchCompletedRef.current = true;
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/dashboard/leave-balance-report');
      const uniqueDepts = [...new Set(response.data.employees.map(emp => emp.department))];
      setDepartments(uniqueDepts.filter(dept => dept));
    } catch (error) {
      console.error('Department list fetch error:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getUsageLevelBadge = (usageLevel, usageRate) => {
    const levelMap = {
      low: { text: 'Low', class: 'badge-danger', threshold: 'Under 50%' },
      medium: { text: 'Medium', class: 'badge-warning', threshold: '50-80%' },
      high: { text: 'High', class: 'badge-success', threshold: '80% and above' }
    };
    const levelInfo = levelMap[usageLevel] || { text: usageLevel, class: 'badge-secondary' };
    return (
      <NoTranslate className={`badge ${levelInfo.class}`} title={levelInfo.threshold}>
        {levelInfo.text} ({usageRate}%)
      </NoTranslate>
    );
  };

  const updateEditingPoolValue = (year, value) => {
    setEditingPoolValues((prev) => ({ ...prev, [String(year)]: value }));
  };

  const updateEditingPoolExpires = (year, value) => {
    setEditingPoolExpires((prev) => ({ ...prev, [String(year)]: value }));
  };

  const handleEdit = (employee) => {
    setEditingId(employee.id);
    const pools = employee.leave_pools || [];
    const init = {};
    const initExp = {};
    if (pools.length === 0) {
      init[String(filters.year)] = '0';
      initExp[String(filters.year)] = '';
    } else {
      for (const p of pools) {
        const y = p.year;
        init[String(y)] =
          p.allocated_days != null && p.allocated_days !== '' ? String(p.allocated_days) : '';
        initExp[String(y)] = poolExpiresToDateInput(p.expires_at);
      }
    }
    setEditingPoolValues(init);
    setEditingPoolExpires(initExp);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingPoolValues({});
    setEditingPoolExpires({});
  };

  const handleSave = async (employee) => {
    const pools = employee.leave_pools || [];
    const years =
      pools.length > 0
        ? [...pools.map((p) => Number(p.year))].sort((a, b) => a - b)
        : [Number(filters.year)];

    try {
      setSaving(true);
      let anyUpdate = false;
      for (const year of years) {
        const raw = editingPoolValues[String(year)];
        const newAllocated = parseFloat(raw);

        if (isNaN(newAllocated)) {
          toast.error(`Please enter a valid number of days for grant year ${year}`);
          return;
        }

        const expiryRaw = (editingPoolExpires[String(year)] ?? '').trim();
        const expiryCandidate = expiryRaw.slice(0, 10);
        if (expiryRaw !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(expiryCandidate)) {
          toast.error(`Invalid expiry date for grant year ${year}`);
          return;
        }
        const newExpiryYmd = /^\d{4}-\d{2}-\d{2}$/.test(expiryCandidate) ? expiryCandidate : '';

        const orig = pools.find((p) => Number(p.year) === year);
        let origExpiryYmd = '';
        if (orig?.expires_at) {
          const s = String(orig.expires_at).trim().slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(s)) origExpiryYmd = s;
        }

        const origAlloc = orig != null ? Number(orig.allocated_days) : NaN;
        const allocUnchanged = orig != null && !Number.isNaN(origAlloc) && origAlloc === newAllocated;
        const expiryUnchanged = origExpiryYmd === newExpiryYmd;

        if (allocUnchanged && expiryUnchanged) continue;

        if (orig != null && newExpiryYmd === '' && origExpiryYmd !== '') {
          toast.error(`Expiry date is required for grant year ${year}`);
          return;
        }

        const body = { year };
        if (!allocUnchanged) body.allocated_days = newAllocated;
        if (!expiryUnchanged) {
          body.expires_at = newExpiryYmd === '' ? null : newExpiryYmd;
        }

        anyUpdate = true;
        await api.put(`/users/${employee.id}/leave-balance`, body);
      }

      if (!anyUpdate) {
        toast.info('No changes to save');
      } else {
        toast.success('Leave balance updated');
      }
      setEditingId(null);
      setEditingPoolValues({});
      setEditingPoolExpires({});
      await fetchReportData();
    } catch (error) {
      console.error('Error updating leave balance:', error);
      toast.error(error.response?.data?.error || 'Failed to update leave balance');
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData || !reportData.employees) return;

    const headers = [
      'Employee ID',
      'Name',
      'Department',
      'Position',
      'Allocated Days (active pools total)',
      `Allocated Days (${filters.year} grant-year pool)`,
      'Used Days (active pools total)',
      'Remaining Days (active pools total)',
      'Usage Rate',
      'Expiry Date (earliest active pool)'
    ];

    const csvData = reportData.employees.map(emp => [
      emp.employee_id,
      emp.full_name,
      emp.department,
      emp.position,
      formatSignedLeaveDays(emp.allocated_days),
      formatSignedLeaveDays(emp.pool_year_allocated_days),
      formatSignedLeaveDays(emp.used_days),
      formatSignedLeaveDays(emp.remaining_days),
      `${leaveUsageRatePercent(emp.used_days, emp.allocated_days).toFixed(1)}%`,
      emp.expires_at ? formatDisplayDate(emp.expires_at) : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leave_balance_report_${filters.year}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMonthlyToCSV = () => {
    if (!monthlyData || !monthlyData.employees) return;

    const headers = [
      'Employee ID',
      'Name',
      'Email',
      'Department',
      'Position',
      'Paid(Approved)',
      'Medical(Approved)',
      'Unpaid(Approved)',
      'Other Paid(Approved)',
      'Compensation(Approved)',
      'Paid(Pending)',
      'Medical(Pending)',
      'Unpaid(Pending)',
      'Other Paid(Pending)',
      'Compensation(Pending)',
      'Allocated(Total)',
      'Used(Total)',
      'Remaining(Total)'
    ];

    const csvData = monthlyData.employees.map((emp) => [
      emp.employee_id,
      emp.full_name,
      emp.email ?? '',
      emp.department,
      emp.position,
      formatSignedLeaveDays(emp.paid_approved),
      formatSignedLeaveDays(emp.medical_approved),
      formatSignedLeaveDays(emp.unpaid_approved),
      formatSignedLeaveDays(emp.other_paid_approved),
      formatSignedLeaveDays(emp.compensation_approved),
      formatSignedLeaveDays(emp.paid_pending),
      formatSignedLeaveDays(emp.medical_pending),
      formatSignedLeaveDays(emp.unpaid_pending),
      formatSignedLeaveDays(emp.other_paid_pending),
      formatSignedLeaveDays(emp.compensation_pending),
      formatSignedLeaveDays(emp.allocated_days),
      formatSignedLeaveDays(emp.used_days),
      formatSignedLeaveDays(emp.remaining_days)
    ]);

    const csvBody = [headers, ...csvData]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const csvContent = `\uFEFF${csvBody}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const mm = String(monthlyMonth).padStart(2, '0');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `monthly_leave_summary_${monthlyYear}-${mm}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openIndividualDetailModal = (employee) => {
    const now = new Date();
    setIndividualScenarioYear(now.getFullYear());
    setIndividualScenarioMonth(now.getMonth() + 1);
    setIndividualScenarioData(null);
    setIndividualDetailEmployee(employee);
  };

  const closeIndividualDetailModal = () => {
    setIndividualDetailEmployee(null);
    setIndividualScenarioData(null);
  };

  const exportIndividualListToCSV = () => {
    if (!reportData || !reportData.employees) return;

    const headers = [
      'Employee ID',
      'Name',
      'Department',
      'Position',
      'Allocated Days (active pools total)',
      `Allocated Days (${filters.year} grant-year pool)`,
      'Used Days (active pools total)',
      'Remaining Days (active pools total)',
      'Usage Rate',
      'Expiry Date (earliest active pool)'
    ];

    const csvData = reportData.employees.map((emp) => {
      const usageRate = leaveUsageRatePercent(emp.used_days, emp.allocated_days).toFixed(1);
      return [
        emp.employee_id,
        emp.full_name,
        emp.department,
        emp.position,
        formatSignedLeaveDays(emp.allocated_days),
        formatSignedLeaveDays(emp.pool_year_allocated_days),
        formatSignedLeaveDays(emp.used_days),
        formatSignedLeaveDays(emp.remaining_days),
        `${usageRate}%`,
        emp.expires_at ? formatDisplayDate(emp.expires_at) : ''
      ];
    });

    const csvBody = [headers, ...csvData]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const csvContent = `\uFEFF${csvBody}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `individual_leave_list_${filters.year}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportIndividualScenarioToCSV = () => {
    if (!individualScenarioData) return;
    const d = individualScenarioData;
    const headers = [
      'employee_id',
      'full_name',
      'department',
      'scenario_last_working_day',
      'annual_leave_days_policy',
      'eligible_entitlement_months',
      'prorated_entitlement_days',
      'paid_leave_used_days_approved_through_scenario',
      'excess_paid_leave_days_for_payroll_deduction',
      'active_pool_allocated_days',
      'active_pool_used_days',
      'active_pool_remaining_days',
      'proration_formula_note'
    ];
    const row = [
      d.employee_id,
      d.full_name,
      d.department ?? '',
      d.scenario_last_working_day ?? '',
      formatSignedLeaveDays(d.annual_leave_days_policy),
      d.eligible_entitlement_months,
      formatSignedLeaveDays(d.prorated_entitlement_days),
      formatSignedLeaveDays(d.paid_leave_used_days_approved_through_scenario),
      formatSignedLeaveDays(d.excess_paid_leave_days_for_payroll_deduction),
      formatSignedLeaveDays(d.active_pool_allocated_days),
      formatSignedLeaveDays(d.active_pool_used_days),
      formatSignedLeaveDays(d.active_pool_remaining_days),
      d.proration_formula_note ?? ''
    ];
    const csvBody = [headers, row]
      .map((cells) =>
        cells.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const csvContent = `\uFEFF${csvBody}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const mm = String(individualScenarioMonth).padStart(2, '0');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `individual_leave_resignation_${d.employee_id}_${individualScenarioYear}-${mm}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportIndividualApplicationsYearToCSV = () => {
    if (!individualScenarioData || !individualScenarioData.leave_applications_for_year) return;
    const list = individualScenarioData.leave_applications_for_year;
    if (!list.length) {
      toast.info('No leave applications to export for this year');
      return;
    }
    const d = individualScenarioData;
    const y = d.applications_list_year ?? individualScenarioYear;
    const headers = [
      'Employee ID',
      'Employee name',
      'Department',
      'List calendar year',
      'Application ID',
      'Start date',
      'End date',
      'Leave type code',
      'Leave type name (EN)',
      'Leave type name (JA)',
      'Status',
      'Total days (from details hrs/8)',
      'Reason',
      'Admin notes',
      'Applied at (UTC)',
      'Reviewed at (UTC)',
      'Reviewed by',
      'Requested approver',
      'Requested approver department',
      'Certificate filename',
      'Compensation attachment filename'
    ];
    const rows = list.map((app) => [
      d.employee_id,
      d.full_name,
      d.department ?? '',
      y,
      app.application_id,
      csvDateYmd(app.start_date),
      csvDateYmd(app.end_date),
      app.leave_type_code ?? '',
      app.leave_type_name_en ?? '',
      app.leave_type_name_ja ?? '',
      app.status ?? '',
      formatSignedLeaveDays(app.total_days),
      app.reason ?? '',
      app.admin_notes ?? '',
      csvIsoOrRaw(app.applied_at),
      csvIsoOrRaw(app.reviewed_at),
      app.reviewed_by_name ?? '',
      app.requested_approver_name ?? '',
      app.requested_approver_department ?? '',
      app.certificate_filename ?? '',
      app.compensation_attachment_filename ?? ''
    ]);
    const csvBody = [headers, ...rows].map((r) => r.map(csvEscapeCell).join(',')).join('\n');
    const csvContent = `\uFEFF${csvBody}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `leave_applications_${d.employee_id}_${y}_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleIndividualScenarioYearChange = (e) => {
    setIndividualScenarioYear(parseInt(e.target.value, 10));
  };

  const handleIndividualScenarioMonthChange = (e) => {
    setIndividualScenarioMonth(parseInt(e.target.value, 10));
  };

  const handleMonthlyTimeChange = (e) => {
    const { name, value } = e.target;
    if (name === 'monthlyYear') {
      setMonthlyYear(parseInt(value, 10));
    } else if (name === 'monthlyMonth') {
      setMonthlyMonth(parseInt(value, 10));
    }
  };

  const { employees, departmentSummary } = reportData || { employees: [], departmentSummary: [] };

  // Department Usage Rate Chart
  const departmentChartData = {
    labels: departmentSummary.map(dept => dept.department),
    datasets: [
      {
        label: 'Average Usage Rate (%)',
        data: departmentSummary.map(dept => parseFloat(dept.avg_usage_rate)),
        backgroundColor: departmentSummary.map(dept => {
          const rate = parseFloat(dept.avg_usage_rate);
          if (rate < 50) return '#dc3545';
          if (rate < 80) return '#ffc107';
          return '#28a745';
        }),
        borderRadius: 4
      }
    ]
  };

  return (
    <div className="leave-balance-report">
      <div className="leave-balance-page-header">
        <div>
          <h1 className="mb-1">Paid Leave Balance Management</h1>
          <small className="text-muted d-block">{APP_VERSION}</small>
        </div>
      </div>

      <div className="leave-balance-tabbar">
        <ul className="leave-balance-tablist" role="tablist">
          <li className="leave-balance-tab-item" role="presentation">
            <button
              type="button"
              id="leave-balance-tab-total"
              role="tab"
              aria-selected={activeTab === 'total'}
              aria-controls="leave-balance-panel-total"
              className={`leave-balance-tab ${activeTab === 'total' ? 'leave-balance-tab--active' : ''}`}
              onClick={() => setActiveTab('total')}
            >
              Total
            </button>
          </li>
          <li className="leave-balance-tab-item" role="presentation">
            <button
              type="button"
              id="leave-balance-tab-monthly"
              role="tab"
              aria-selected={activeTab === 'monthly'}
              aria-controls="leave-balance-panel-monthly"
              className={`leave-balance-tab ${activeTab === 'monthly' ? 'leave-balance-tab--active' : ''}`}
              onClick={() => setActiveTab('monthly')}
            >
              Monthly
            </button>
          </li>
          <li className="leave-balance-tab-item" role="presentation">
            <button
              type="button"
              id="leave-balance-tab-individual"
              role="tab"
              aria-selected={activeTab === 'individual'}
              aria-controls="leave-balance-panel-individual"
              className={`leave-balance-tab ${activeTab === 'individual' ? 'leave-balance-tab--active' : ''}`}
              onClick={() => setActiveTab('individual')}
            >
              Individual
            </button>
          </li>
        </ul>
      </div>

      <div className="leave-balance-csv-toolbar" aria-label="Export actions">
        {activeTab === 'total' && (
          <button type="button" onClick={exportToCSV} className="btn btn-success btn-sm">
            CSV Export
          </button>
        )}
        {activeTab === 'individual' && (
          <button type="button" onClick={exportIndividualListToCSV} className="btn btn-success btn-sm">
            CSV Export
          </button>
        )}
        {activeTab === 'monthly' && (
          <button type="button" onClick={exportMonthlyToCSV} className="btn btn-success btn-sm">
            CSV Export
          </button>
        )}
      </div>

      <div
        id="leave-balance-panel-total"
        role="tabpanel"
        aria-labelledby="leave-balance-tab-total"
        hidden={activeTab !== 'total'}
        className="leave-balance-tab-panel"
      >
      {activeTab === 'total' && loading && (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      )}
      {activeTab === 'total' && !loading && !reportData && (
        <div className="alert alert-danger">Failed to load data</div>
      )}
      {activeTab === 'total' && !loading && reportData && (
      <>
      {/* Filters */}
      <div className="card mb-4">
        <h3>Filters</h3>
        <p className="text-muted small mb-2">
          <strong>Table totals</strong> (used / remaining / usage rate) are sums of <strong>all non-expired</strong> leave pools.
          The <strong>report year</strong> drop-down chooses which grant-year pool is highlighted in the table when not editing, and which year is used to <strong>create the first pool</strong> when an employee has no <code>annual_leave_balance</code> rows yet.
        </p>
        <div className="grid grid-3">
          <div className="form-group">
            <label className="form-label">Report year (highlight / new pool)</label>
            <select
              name="year"
              className="form-control"
              value={filters.year}
              onChange={handleFilterChange}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              name="department"
              className="form-control notranslate"
              translate="no"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="leave-balance-employee-search">
              Employee search
            </label>
            <input
              id="leave-balance-employee-search"
              type="search"
              className="form-control"
              placeholder="Employee ID, Name, or Email"
              value={employeeSearchInput}
              onChange={(e) => setEmployeeSearchInput(e.target.value)}
              autoComplete="off"
            />
            <small className="text-muted d-block mt-1">
              Shows rows where the text appears in Employee ID, Name, or Email (any match).
            </small>
            <small className="text-muted d-block">{employees.length} people</small>
          </div>
        </div>
      </div>

      {/* Department Summary */}
      <div className="card mb-4">
        <h3>Department Summary</h3>
        <div className="summary-grid">
          {departmentSummary.map(dept => (
            <div key={dept.department} className="summary-card">
              <NoTranslate as="h4">{dept.department}</NoTranslate>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Employees</span>
                  <span className="stat-value">{dept.total_employees}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Average Usage Rate</span>
                  <span className="stat-value">{dept.avg_usage_rate}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Low Usage</span>
                  <span className="stat-value warning">{dept.low_usage_count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Department Usage Rate Chart */}
      <div className="card mb-4">
        <h3>Average Usage Rate by Department</h3>
        <div style={{ height: '300px' }}>
          <Bar 
            data={departmentChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      return `Average Usage Rate: ${context.parsed.y}%`;
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                  ticks: {
                    callback: (value) => `${value}%`
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Individual Details */}
      <div className="card">
        <h3>Individual Details</h3>
        <p className="text-muted small">
          Per-employee <strong>used / remaining / usage rate</strong> use <strong>active-pool totals</strong> (earliest pool expiry in Expiry).
          When you click Edit, <strong>each grant-year pool</strong> for that employee gets its own allocated-days field and expiry date. Saving only sends grant years you changed (or creates the filter year if there are no rows yet).
        </p>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Department</th>
                <th>Position</th>
                <th className="leave-balance-col-allocated" title="Total = sum of active pools; Edit shows every grant-year pool.">Allocated</th>
                <th>Used (active pools total)</th>
                <th>Remaining (active pools total)</th>
                <th>Usage Rate</th>
                <th>Usage Level</th>
                <th>Expiry Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(employee => {
                const usageRateNum = leaveUsageRatePercent(employee.used_days, employee.allocated_days);
                const usageRate = usageRateNum.toFixed(1);
                const isEditing = editingId === employee.id;
                const sortedPools = [...(employee.leave_pools || [])].sort(
                  (a, b) => Number(a.year) - Number(b.year)
                );
                return (
                  <tr key={employee.id} className={employee.usage_level === 'low' ? 'low-usage-row' : ''}>
                    <td><NoTranslate>{employee.employee_id}</NoTranslate></td>
                    <td><NoTranslate>{employee.full_name}</NoTranslate></td>
                    <td><NoTranslate>{employee.department}</NoTranslate></td>
                    <td><NoTranslate>{employee.position}</NoTranslate></td>
                    <td className="leave-balance-col-allocated">
                      {isEditing ? (
                        <div className="d-flex flex-column align-items-stretch" style={{ minWidth: 'max-content' }}>
                          {sortedPools.length === 0 ? (
                            <div className="mb-0" style={{ minWidth: 'max-content' }}>
                              <div className="d-flex align-items-center" style={{ gap: '0.35rem', flexWrap: 'nowrap' }}>
                                <span className="small" style={{ whiteSpace: 'nowrap' }}>{filters.year}</span>
                                <input
                                  type="number"
                                  step="0.5"
                                  className="form-control form-control-sm"
                                  style={{ width: '68px', minWidth: '68px', maxWidth: '68px' }}
                                  value={editingPoolValues[String(filters.year)] ?? ''}
                                  onChange={(e) => updateEditingPoolValue(filters.year, e.target.value)}
                                  aria-label={`Allocated days for grant year ${filters.year}`}
                                />
                              </div>
                              <div className="d-flex align-items-center mt-1" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
                                <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>Expiry</span>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  style={{ width: 'auto', minWidth: '9.5rem' }}
                                  value={editingPoolExpires[String(filters.year)] ?? ''}
                                  onChange={(e) => updateEditingPoolExpires(filters.year, e.target.value)}
                                  aria-label={`Expiry date for grant year ${filters.year}`}
                                />
                              </div>
                            </div>
                          ) : (
                            sortedPools.map((p) => {
                              return (
                                <div key={p.year} className="mb-2" style={{ minWidth: 'max-content' }}>
                                  <div className="d-flex align-items-center" style={{ gap: '0.35rem', flexWrap: 'nowrap' }}>
                                    <span className="small" style={{ whiteSpace: 'nowrap' }}>{p.year}</span>
                                    <input
                                      type="number"
                                      step="0.5"
                                      className="form-control form-control-sm"
                                      style={{ width: '68px', minWidth: '68px', maxWidth: '68px' }}
                                      value={editingPoolValues[String(p.year)] ?? ''}
                                      onChange={(e) => updateEditingPoolValue(p.year, e.target.value)}
                                      aria-label={`Allocated days for grant year ${p.year}`}
                                    />
                                  </div>
                                  <div className="d-flex align-items-center mt-1" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
                                    <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>Expiry</span>
                                    <input
                                      type="date"
                                      className="form-control form-control-sm"
                                      style={{ width: 'auto', minWidth: '9.5rem' }}
                                      value={editingPoolExpires[String(p.year)] ?? ''}
                                      onChange={(e) => updateEditingPoolExpires(p.year, e.target.value)}
                                      aria-label={`Expiry date for grant year ${p.year}`}
                                    />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      ) : (
                        <span>{formatSignedLeaveDays(employee.allocated_days)} days</span>
                      )}
                    </td>
                    <td>{formatSignedLeaveDays(employee.used_days)} days</td>
                    <td>{formatSignedLeaveDays(employee.remaining_days)} days</td>
                    <td>
                      <div className="usage-rate">
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar" 
                            style={{ 
                              width: `${Math.min(usageRateNum, 100)}%`,
                              backgroundColor: usageRateNum < 50 ? '#dc3545' : usageRateNum < 80 ? '#ffc107' : '#28a745'
                            }}
                          ></div>
                        </div>
                        <span className="rate-text">{usageRate}%</span>
                      </div>
                    </td>
                    <td>{getUsageLevelBadge(employee.usage_level, usageRate)}</td>
                    <td>
                      {employee.expires_at 
                        ? formatDisplayDate(employee.expires_at)
                        : '-'
                      }
                    </td>
                    <td>
                      {isEditing ? (
                        <div className="edit-actions">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleSave(employee)}
                            disabled={saving}
                            style={{ marginRight: '4px' }}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={handleCancel}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(employee)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
      </div>

      <div
        id="leave-balance-panel-individual"
        role="tabpanel"
        aria-labelledby="leave-balance-tab-individual"
        hidden={activeTab !== 'individual'}
        className="leave-balance-tab-panel"
      >
      {activeTab === 'individual' && loading && (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      )}
      {activeTab === 'individual' && !loading && !reportData && (
        <div className="alert alert-danger">Failed to load data</div>
      )}
      {activeTab === 'individual' && !loading && reportData && (
      <>
      <div className="card mb-4">
        <h3>Filters</h3>
        <p className="text-muted small mb-2">
          Rosters align with Total. Employee search matches Employee ID, Name, or Email. Use{' '}
          <strong>Detail</strong> to assume the hypothetical last working day is the{' '}
          <strong>last calendar day</strong> of a selected month — prorated annual entitlement vs approved Paid Leave from Jan 1 of that year through that date (see modal note).
        </p>
        <div className="grid grid-3">
          <div className="form-group">
            <label className="form-label">Report year (grant-year highlight)</label>
            <select
              name="year"
              className="form-control"
              value={filters.year}
              onChange={handleFilterChange}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              name="department"
              className="form-control notranslate"
              translate="no"
              value={filters.department}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="leave-balance-individual-employee-search">
              Employee search
            </label>
            <input
              id="leave-balance-individual-employee-search"
              type="search"
              className="form-control"
              placeholder="Employee ID, Name, or Email"
              value={employeeSearchInput}
              onChange={(e) => setEmployeeSearchInput(e.target.value)}
              autoComplete="off"
            />
            <small className="text-muted d-block mt-1">
              Showing {employees.length} people.
            </small>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>List</h3>
        <p className="text-muted small">
          Columns match Total balances (active pools). Edit pools on the Total tab — here you can inspect resignation impact per person via Detail.
        </p>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Department</th>
                <th>Position</th>
                <th>Allocated</th>
                <th>Used</th>
                <th>Remaining</th>
                <th>Usage Rate</th>
                <th>Usage Level</th>
                <th>Expiry Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => {
                const usageRateNum = leaveUsageRatePercent(employee.used_days, employee.allocated_days);
                const usageRate = usageRateNum.toFixed(1);
                return (
                  <tr key={employee.id} className={employee.usage_level === 'low' ? 'low-usage-row' : ''}>
                    <td><NoTranslate>{employee.employee_id}</NoTranslate></td>
                    <td><NoTranslate>{employee.full_name}</NoTranslate></td>
                    <td><NoTranslate>{employee.department}</NoTranslate></td>
                    <td><NoTranslate>{employee.position}</NoTranslate></td>
                    <td>{formatSignedLeaveDays(employee.allocated_days)} days</td>
                    <td>{formatSignedLeaveDays(employee.used_days)} days</td>
                    <td>{formatSignedLeaveDays(employee.remaining_days)} days</td>
                    <td>
                      <div className="usage-rate">
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar"
                            style={{
                              width: `${Math.min(usageRateNum, 100)}%`,
                              backgroundColor:
                                usageRateNum < 50 ? '#dc3545' : usageRateNum < 80 ? '#ffc107' : '#28a745'
                            }}
                          ></div>
                        </div>
                        <span className="rate-text">{usageRate}%</span>
                      </div>
                    </td>
                    <td>{getUsageLevelBadge(employee.usage_level, usageRate)}</td>
                    <td>
                      {employee.expires_at
                        ? formatDisplayDate(employee.expires_at)
                        : '-'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => openIndividualDetailModal(employee)}
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
      </div>

      <div
        id="leave-balance-panel-monthly"
        role="tabpanel"
        aria-labelledby="leave-balance-tab-monthly"
        hidden={activeTab !== 'monthly'}
        className="leave-balance-tab-panel"
      >
      {activeTab === 'monthly' && monthlyLoading && (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      )}
      {activeTab === 'monthly' && !monthlyLoading && !monthlyData && (
        <div className="alert alert-danger">Failed to load data</div>
      )}
      {activeTab === 'monthly' && !monthlyLoading && monthlyData && (
        <>
          <div className="card mb-4">
            <h3>Filters</h3>
            <p className="text-muted small mb-2">
              Ten month columns: for each of the five leave types (form order: Paid → Medical → Unpaid → Other Paid → Compensation),
              days from <strong>approved</strong> and <strong>pending</strong> applications whose <strong>leave dates</strong> fall in the selected range (hours ÷ 8).
              <strong> Allocated(Total) / Used(Total) / Remaining(Total)</strong> are <strong>overall</strong> active paid-leave pool
              totals (non-expired rows), same as Total — not limited to that month. Those three columns appear last in the table and CSV.
            </p>
            <div className="grid grid-3">
              <div className="form-group">
                <label className="form-label" htmlFor="mls-year">Year</label>
                <select
                  id="mls-year"
                  name="monthlyYear"
                  className="form-control"
                  value={monthlyYear}
                  onChange={handleMonthlyTimeChange}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="mls-month">Month</label>
                <select
                  id="mls-month"
                  name="monthlyMonth"
                  className="form-control"
                  value={monthlyMonth}
                  onChange={handleMonthlyTimeChange}
                >
                  {Array.from({ length: 12 }, (_, i) => {
                    const m = i + 1;
                    return (
                      <option key={m} value={m}>
                        {m.toString().padStart(2, '0')}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <select
                  name="department"
                  className="form-control notranslate"
                  translate="no"
                  value={filters.department}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label" htmlFor="mls-employee-search">
                  Employee search
                </label>
                <input
                  id="mls-employee-search"
                  type="search"
                  className="form-control"
                  placeholder="Employee ID, Name, or Email"
                  value={employeeSearchInput}
                  onChange={(e) => setEmployeeSearchInput(e.target.value)}
                  autoComplete="off"
                />
                <small className="text-muted d-block mt-1">
                  Period: <NoTranslate as="span">{monthlyData.month_start}</NoTranslate> –{' '}
                  <NoTranslate as="span">{monthlyData.month_end}</NoTranslate>.{' '}
                  {monthlyData.employees.length} people.
                </small>
              </div>
            </div>
          </div>
          <div className="card">
            <h3>Preview</h3>
            <div className="table-responsive">
              <table className="table table-striped table-sm">
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <LeaveMonthBracketHeader label="Paid" bracket="Approved" />
                    <LeaveMonthBracketHeader label="Medical" bracket="Approved" />
                    <LeaveMonthBracketHeader label="Unpaid" bracket="Approved" />
                    <LeaveMonthBracketHeader label="Other Paid" bracket="Approved" />
                    <LeaveMonthBracketHeader label="Compensation" bracket="Approved" />
                    <LeaveMonthBracketHeader label="Paid" bracket="Pending" />
                    <LeaveMonthBracketHeader label="Medical" bracket="Pending" />
                    <LeaveMonthBracketHeader label="Unpaid" bracket="Pending" />
                    <LeaveMonthBracketHeader label="Other Paid" bracket="Pending" />
                    <LeaveMonthBracketHeader label="Compensation" bracket="Pending" />
                    <LeaveMonthBracketHeader label="Allocated" bracket="Total" />
                    <LeaveMonthBracketHeader label="Used" bracket="Total" />
                    <LeaveMonthBracketHeader label="Remaining" bracket="Total" />
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.employees.map((emp) => (
                    <tr key={emp.id}>
                      <td><NoTranslate>{emp.employee_id}</NoTranslate></td>
                      <td><NoTranslate>{emp.full_name}</NoTranslate></td>
                      <td>{formatSignedLeaveDays(emp.paid_approved)}</td>
                      <td>{formatSignedLeaveDays(emp.medical_approved)}</td>
                      <td>{formatSignedLeaveDays(emp.unpaid_approved)}</td>
                      <td>{formatSignedLeaveDays(emp.other_paid_approved)}</td>
                      <td>{formatSignedLeaveDays(emp.compensation_approved)}</td>
                      <td>{formatSignedLeaveDays(emp.paid_pending)}</td>
                      <td>{formatSignedLeaveDays(emp.medical_pending)}</td>
                      <td>{formatSignedLeaveDays(emp.unpaid_pending)}</td>
                      <td>{formatSignedLeaveDays(emp.other_paid_pending)}</td>
                      <td>{formatSignedLeaveDays(emp.compensation_pending)}</td>
                      <td>{formatSignedLeaveDays(emp.allocated_days)}</td>
                      <td>{formatSignedLeaveDays(emp.used_days)}</td>
                      <td>{formatSignedLeaveDays(emp.remaining_days)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </div>

      {individualDetailEmployee ? (
        <div
          className="leave-balance-modal-overlay"
          onClick={closeIndividualDetailModal}
          role="presentation"
        >
          <div
            className="leave-balance-modal-content leave-balance-modal-layout"
            role="dialog"
            aria-modal="true"
            aria-labelledby="individual-leave-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="leave-balance-modal-close-row">
              <button
                type="button"
                className="leave-balance-modal-close-btn"
                onClick={closeIndividualDetailModal}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="leave-balance-modal-header">
            <h3 id="individual-leave-modal-title">Individual resignation check</h3>
            <p className="mb-3 text-muted small">
              <NoTranslate>{individualDetailEmployee.full_name}</NoTranslate>{' '}
              (<NoTranslate>{individualDetailEmployee.employee_id}</NoTranslate>)
            </p>
            <div className="leave-balance-modal-grid mb-3">
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="ind-resign-year">
                  Scenario year (last working day at month-end)
                </label>
                <select
                  id="ind-resign-year"
                  className="form-control"
                  value={individualScenarioYear}
                  onChange={handleIndividualScenarioYearChange}
                >
                  {Array.from({ length: 6 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group mb-2">
                <label className="form-label" htmlFor="ind-resign-month">
                  Scenario month
                </label>
                <select
                  id="ind-resign-month"
                  className="form-control"
                  value={individualScenarioMonth}
                  onChange={handleIndividualScenarioMonthChange}
                >
                  {Array.from({ length: 12 }, (_, i) => {
                    const m = i + 1;
                    return (
                      <option key={m} value={m}>
                        {String(m).padStart(2, '0')}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="leave-balance-modal-actions mb-3">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportIndividualScenarioToCSV}
                disabled={!individualScenarioData || individualScenarioLoading}
              >
                CSV Download
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={closeIndividualDetailModal}
              >
                Close
              </button>
            </div>

            </div>

            <div className="leave-balance-modal-body">
              {individualScenarioLoading ? (
                <div className="loading">
                  <div className="spinner" />
                </div>
              ) : null}

              {!individualScenarioLoading && individualScenarioData ? (
                <>
                  <div className="leave-balance-modal-summary-block small">
                    <table className="table table-bordered table-sm mb-2">
                      <tbody>
                        <tr>
                          <th scope="row">Hypothetical last day</th>
                          <td>
                            <NoTranslate>{individualScenarioData.scenario_last_working_day}</NoTranslate>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Hire date</th>
                          <td><NoTranslate>{individualScenarioData.hire_date ?? '—'}</NoTranslate></td>
                        </tr>
                        <tr>
                          <th scope="row">Annual entitlement (policy days)</th>
                          <td>
                            {formatSignedLeaveDays(individualScenarioData.annual_leave_days_policy)} days
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Eligible months (this year thru scenario)</th>
                          <td>{individualScenarioData.eligible_entitlement_months}</td>
                        </tr>
                        <tr>
                          <th scope="row">Prorated entitlement by then</th>
                          <td>
                            <strong>
                              {formatSignedLeaveDays(individualScenarioData.prorated_entitlement_days)} days
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Approved Paid Leave used (Jan 1 → that date)</th>
                          <td>
                            {formatSignedLeaveDays(
                              individualScenarioData.paid_leave_used_days_approved_through_scenario
                            )}{' '}
                            days
                          </td>
                        </tr>
                        <tr
                          className={
                            Number(individualScenarioData.excess_paid_leave_days_for_payroll_deduction) > 0
                              ? 'leave-balance-excess-row'
                              : ''
                          }
                        >
                          <th scope="row">Excess over entitlement (salary deduction guide)</th>
                          <td>
                            <strong>
                              {formatSignedLeaveDays(
                                individualScenarioData.excess_paid_leave_days_for_payroll_deduction
                              )}{' '}
                              days
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-muted mb-2">
                      Current active paid-leave pools: allocated{' '}
                      {formatSignedLeaveDays(individualScenarioData.active_pool_allocated_days)}, used{' '}
                      {formatSignedLeaveDays(individualScenarioData.active_pool_used_days)}, remaining{' '}
                      {formatSignedLeaveDays(individualScenarioData.active_pool_remaining_days)}.
                    </p>
                    <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                      {individualScenarioData.proration_formula_note}
                    </p>
                  </div>

                  <div className="leave-balance-modal-apps-section">
                    <hr className="leave-balance-modal-divider" />
                    <div className="leave-balance-modal-apps-title-row">
                      <h4 className="leave-balance-modal-apps-title">
                        Leave applications in{' '}
                        <NoTranslate>
                          {individualScenarioData.applications_list_year ?? individualScenarioYear}
                        </NoTranslate>
                        <span className="leave-balance-modal-apps-subtitle"> ({'all statuses'})</span>
                      </h4>
                      <button
                        type="button"
                        className="btn btn-success btn-sm leave-balance-modal-apps-csv"
                        title="Detailed CSV incl. reviewers and notes"
                        onClick={exportIndividualApplicationsYearToCSV}
                        disabled={
                          individualScenarioLoading ||
                          !individualScenarioData.leave_applications_for_year ||
                          individualScenarioData.leave_applications_for_year.length === 0
                        }
                      >
                        CSV Download
                      </button>
                    </div>
                    {!individualScenarioData.leave_applications_for_year ||
                    individualScenarioData.leave_applications_for_year.length === 0 ? (
                      <p className="text-muted small mb-0">
                        No applications with start date in this calendar year.
                      </p>
                    ) : (
                      <div className="leave-balance-apps-table-scroll">
                        <table className="table table-striped table-bordered table-sm leave-balance-apps-table">
                          <thead>
                            <tr>
                              <th scope="col">ID</th>
                              <th scope="col">Start</th>
                              <th scope="col">End</th>
                              <th scope="col">Type</th>
                              <th scope="col">Days</th>
                              <th scope="col">Status</th>
                              <th scope="col">Applied</th>
                              <th scope="col">Reviewed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {individualScenarioData.leave_applications_for_year.map((app) => (
                              <tr key={app.application_id}>
                                <td><NoTranslate>{app.application_id}</NoTranslate></td>
                                <td>
                                  <NoTranslate>{formatScenarioDateOnly(app.start_date)}</NoTranslate>
                                </td>
                                <td><NoTranslate>{formatScenarioDateOnly(app.end_date)}</NoTranslate></td>
                                <td>
                                  <NoTranslate>
                                    {app.leave_type_name_en ||
                                      app.leave_type_name_ja ||
                                      app.leave_type_code ||
                                      '—'}
                                  </NoTranslate>
                                </td>
                                <td>{formatSignedLeaveDays(app.total_days)}</td>
                                <td><NoTranslate>{app.status ?? '—'}</NoTranslate></td>
                                <td><NoTranslate>{formatScenarioDateTimeShort(app.applied_at)}</NoTranslate></td>
                                <td>
                                  <NoTranslate>{formatScenarioDateTimeShort(app.reviewed_at)}</NoTranslate>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .leave-balance-report .leave-balance-page-header {
          margin-bottom: 0.25rem;
        }
        .leave-balance-report .leave-balance-tabbar {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          margin-top: 1rem;
          border-bottom: 1px solid #ced4da;
        }
        .leave-balance-report .leave-balance-tablist {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 0;
        }
        .leave-balance-report .leave-balance-csv-toolbar {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding: 0.6rem 0 0.35rem;
        }
        .leave-balance-report .leave-balance-tab-item {
          list-style: none;
          margin: 0;
          padding: 0;
          margin-bottom: -1px;
        }
        .leave-balance-report .leave-balance-tab {
          appearance: none;
          -webkit-appearance: none;
          font: inherit;
          font-size: 0.95rem;
          font-weight: 500;
          line-height: 1.5;
          cursor: pointer;
          margin: 0;
          padding: 0.55rem 1.15rem;
          border: 1px solid transparent;
          border-bottom: none;
          border-radius: 0.35rem 0.35rem 0 0;
          background: #e9ecef;
          color: #495057;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .leave-balance-report .leave-balance-tab:hover:not(.leave-balance-tab--active) {
          background: #dee2e6;
          color: #212529;
        }
        .leave-balance-report .leave-balance-tab:focus-visible {
          outline: 2px solid #86b7fe;
          outline-offset: 2px;
          z-index: 1;
          position: relative;
        }
        .leave-balance-report .leave-balance-tab--active {
          background: #fff;
          color: #212529;
          border-color: #ced4da;
          border-bottom: 1px solid #f5f5f5;
          margin-bottom: -1px;
          padding-bottom: calc(0.55rem + 1px);
        }
        .leave-balance-report .leave-balance-tab-item + .leave-balance-tab-item .leave-balance-tab:not(.leave-balance-tab--active) {
          margin-left: 2px;
        }
        .leave-balance-report .leave-balance-tab-panel {
          padding-top: 0.35rem;
        }
        .leave-balance-report .leave-balance-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .leave-balance-report .leave-balance-modal-content {
          background: #fff;
          border-radius: 8px;
          max-width: min(1180px, 96vw);
          width: 100%;
          max-height: min(920px, 92vh);
          overflow: hidden;
          padding: 1.25rem 1.5rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
          min-height: 0;
          position: relative;
        }
        .leave-balance-report .leave-balance-modal-close-row {
          display: flex;
          justify-content: flex-end;
          margin: -0.35rem -0.2rem 0.25rem;
        }
        .leave-balance-report .leave-balance-modal-close-btn {
          appearance: none;
          -webkit-appearance: none;
          margin: 0;
          padding: 0.2rem 0.55rem;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: #6c757d;
          font-size: 1.65rem;
          line-height: 1;
          cursor: pointer;
          font-weight: 300;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .leave-balance-report .leave-balance-modal-close-btn:hover {
          background-color: #e9ecef;
          color: #212529;
        }
        .leave-balance-report .leave-balance-modal-close-btn:focus-visible {
          outline: 2px solid #86b7fe;
          outline-offset: 2px;
        }
        .leave-balance-report .leave-balance-modal-layout {
          gap: 0;
        }
        .leave-balance-report .leave-balance-modal-divider {
          border: none;
          border-top: 1px solid #dee2e6;
          margin: 0 0 0.75rem;
        }
        .leave-balance-report .leave-balance-modal-header {
          flex-shrink: 0;
        }
        .leave-balance-report .leave-balance-modal-body {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .leave-balance-report .leave-balance-modal-summary-block {
          flex: 0 1 auto;
          max-height: min(340px, 36vh);
          overflow-y: auto;
          padding-right: 0.25rem;
        }
        .leave-balance-report .leave-balance-modal-apps-section {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .leave-balance-report .leave-balance-modal-apps-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 0.5rem;
        }
        .leave-balance-report .leave-balance-modal-apps-title-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.35rem 0.85rem;
          margin-bottom: 0.35rem;
        }
        .leave-balance-report .leave-balance-modal-apps-title-row .leave-balance-modal-apps-title {
          flex: 1;
          margin-bottom: 0;
          min-width: min(280px, 100%);
        }
        .leave-balance-report .leave-balance-modal-apps-csv {
          flex-shrink: 0;
          white-space: nowrap;
        }
        .leave-balance-report .leave-balance-modal-apps-subtitle {
          font-weight: normal;
          font-size: 0.85rem;
          color: #6c757d;
        }
        .leave-balance-report .leave-balance-apps-table-scroll {
          flex: 1;
          min-height: 120px;
          overflow: auto;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          margin-top: 0.35rem;
        }
        .leave-balance-report .leave-balance-apps-table {
          margin-bottom: 0;
          width: max(100%, 720px);
          border-collapse: separate;
          border-spacing: 0;
        }
        .leave-balance-report .leave-balance-apps-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f1f3f5;
          box-shadow: 0 1px 0 #dee2e6;
          font-weight: 600;
          white-space: nowrap;
        }
        .leave-balance-report .leave-balance-apps-table th,
        .leave-balance-report .leave-balance-apps-table td {
          padding: 0.45rem 0.55rem;
          vertical-align: middle;
        }
        .leave-balance-report .leave-balance-modal-actions .btn {
          margin-right: 0.35rem;
          margin-bottom: 0.35rem;
        }
        .leave-balance-report .leave-balance-modal-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.75rem;
        }
        @media (max-width: 576px) {
          .leave-balance-report .leave-balance-modal-grid {
            grid-template-columns: 1fr;
          }
        }
        .leave-balance-report .leave-balance-excess-row {
          background-color: #fff8e6;
        }

        .employee-count {
          padding: 0.5rem;
          background-color: #f8f9fa;
          border-radius: 4px;
          font-weight: 500;
          color: #495057;
        }
        
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }
        
        .summary-card {
          background-color: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          border-left: 4px solid #007bff;
        }
        
        .summary-card h4 {
          margin-bottom: 1rem;
          color: #495057;
        }
        
        .summary-stats {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .stat-label {
          color: #6c757d;
          font-size: 0.9rem;
        }
        
        .stat-value {
          font-weight: bold;
          color: #495057;
        }
        
        .stat-value.warning {
          color: #dc3545;
        }
        
        .usage-rate {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .progress-bar-container {
          flex: 1;
          height: 20px;
          background-color: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        
        .progress-bar {
          height: 100%;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        .rate-text {
          font-weight: 500;
          min-width: 45px;
          text-align: right;
        }
        
        .low-usage-row {
          background-color: #fff5f5;
        }
        
        .low-usage-row:hover {
          background-color: #fed7d7;
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        .table {
          font-size: 0.9rem;
        }
        
        .table th {
          background-color: #f8f9fa;
          font-weight: 600;
          border-top: none;
        }

        .leave-balance-report .table th.leave-balance-mcol {
          text-align: center;
          white-space: normal;
          vertical-align: bottom;
          max-width: 5.75rem;
          line-height: 1.25;
        }
        
        .table th.leave-balance-col-allocated,
        .table td.leave-balance-col-allocated {
          min-width: 200px;
          box-sizing: border-box;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        .edit-allocated-days {
          display: flex;
          align-items: center;
        }
        
        .edit-actions {
          display: flex;
          gap: 4px;
        }
        
        @media (max-width: 768px) {
          .summary-grid {
            grid-template-columns: 1fr;
          }
          
          .table {
            font-size: 0.8rem;
          }
          
          .usage-rate {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .progress-bar-container {
            width: 100px;
          }
          
          .rate-text {
            min-width: auto;
          }
        }
        
        @media (max-width: 576px) {
          .table th,
          .table td {
            padding: 0.5rem 0.25rem;
          }
          
          .summary-card {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default LeaveBalanceReport;
